import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth, isRefreshFor } from './AuthContext';
import {
  InboxIcon,
  SendIcon,
  FileIcon,
  TrashIcon,
  FlagIcon,
  StarIcon,
  ReplyIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MailIcon,
} from './Icons';
import { avatarColor, avatarInitials, formatFileSize } from './emailAvatar';
import EmailComposer from './EmailComposer';
import ListSkeleton from './LoadingSkeleton';
import { fullTimestamp, listTimestamp, replySubject } from './emailUtils';

const PER_PAGE = 25;
// Hostinger is asked again at most this often, however many live events
// arrive, and only while this tab is visible.
const REFRESH_MS = 60000;
const MIN_GAP_MS = 15000;

// specialUse is Hostinger's RFC 6154 tag on each folder — a backslash-
// prefixed, capitalized string (e.g. '\Inbox', '\Sent', '\Drafts', '\Trash',
// '\Junk'), confirmed against a real mailbox rather than assumed from docs.
// normalizeSpecialUse strips the backslash and lowercases it for lookup.
const normalizeSpecialUse = (specialUse) => (specialUse || '').replace(/^\\/, '').toLowerCase();

const FOLDER_META = {
  inbox: { label: 'Inbox', icon: InboxIcon },
  sent: { label: 'Sent', icon: SendIcon },
  drafts: { label: 'Drafts', icon: FileIcon },
  trash: { label: 'Trash', icon: TrashIcon },
  junk: { label: 'Spam', icon: FlagIcon },
};
const FOLDER_ORDER = ['inbox', 'sent', 'drafts', 'junk', 'trash'];

const normalizeCid = (value) => {
  const cid = String(value || '').trim().replace(/^</, '').replace(/>$/, '');
  try {
    return decodeURIComponent(cid).toLowerCase();
  } catch {
    return cid.toLowerCase();
  }
};

// Inline images (Gmail's pasted/photographed ones) arrive as
// <img src="cid:..."> pointing at one of the message's attachments, which a
// browser can't load. Points each one at the attachment download route
// instead (no ?name=, so it's served inline rather than as a download).
const withInlineImages = (html, attachments, attachmentUrl) => {
  const byCid = {};
  (attachments || []).forEach((attachment) => {
    if (attachment.contentId) byCid[normalizeCid(attachment.contentId)] = attachmentUrl(attachment);
  });
  return html.replace(/(\bsrc\s*=\s*)(["']?)cid:([^"'\s>]+)\2/gi, (whole, prefix, quote, cid) => {
    const url = byCid[normalizeCid(cid)];
    return url ? `${prefix}"${url}"` : whole;
  });
};

const folderMeta = (folder) => {
  if (!folder) return { label: '', icon: InboxIcon };
  const key = normalizeSpecialUse(folder.specialUse);
  if (FOLDER_META[key]) return FOLDER_META[key];
  if (folder.path === 'INBOX') return FOLDER_META.inbox;
  return { label: folder.name || folder.path, icon: FileIcon };
};

// Inbox first, then the well-known special-use folders in a fixed order,
// then any other custom folder alphabetically.
const sortFolders = (folders) =>
  [...folders].sort((a, b) => {
    const rank = (folder) => {
      const index = FOLDER_ORDER.indexOf(normalizeSpecialUse(folder.specialUse));
      return index === -1 ? FOLDER_ORDER.length : index;
    };
    const diff = rank(a) - rank(b);
    if (diff !== 0) return diff;
    return (a.name || a.path).localeCompare(b.name || b.path);
  });

const isStarred = (flags) => (flags || []).some((flag) => /flagged/i.test(flag));
const addressText = (address) => (address ? address.name || address.address : '');

// A real IMAP mailbox browser (Inbox/Sent/Drafts/Trash/Spam + any custom
// folders), live against Hostinger's Mail API — as opposed to the
// Conversations view, which groups mail by customer from the CRM's own
// record. This shows everything in the mailbox, as Hostinger's webmail
// would, including mail the CRM never touched.
function EmailMailbox({ signature = '' }) {
  const { authFetch, subscribeRefresh } = useAuth();
  const [folders, setFolders] = useState([]);
  const [foldersError, setFoldersError] = useState('');
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [messages, setMessages] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [selectedUid, setSelectedUid] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [messageLoading, setMessageLoading] = useState(false);
  const [messageError, setMessageError] = useState('');
  const [actionError, setActionError] = useState('');
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyNotice, setReplyNotice] = useState('');
  const lastRefreshRef = useRef(0);
  const viewRef = useRef({ folder: null, page: 1 });
  viewRef.current = { folder: selectedFolder, page: pagination.page };

  const loadFolders = useCallback(
    async ({ selectInbox = false } = {}) => {
      try {
        const response = await authFetch('/api/email/mailbox/folders');
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to load folders');
        const list = Array.isArray(data) ? data : [];
        setFolders(sortFolders(list));
        setFoldersError('');
        if (selectInbox) {
          const inbox = list.find((folder) => folder.path === 'INBOX');
          setSelectedFolder(inbox ? inbox.path : list[0]?.path || null);
        }
      } catch (error) {
        setFoldersError(error.message || 'Failed to load folders');
      }
    },
    [authFetch]
  );

  useEffect(() => {
    loadFolders({ selectInbox: true });
  }, [loadFolders]);

  const fetchMessages = useCallback(
    async (folder, page = 1, { silent = false } = {}) => {
      if (!folder) return;
      if (!silent) {
        setListLoading(true);
        setListError('');
      }
      try {
        const response = await authFetch(
          `/api/email/mailbox/folders/${encodeURIComponent(folder)}/messages?page=${page}&perPage=${PER_PAGE}`
        );
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to load messages');
        if (viewRef.current.folder !== folder) return;
        setMessages(Array.isArray(data.data) ? data.data : []);
        setPagination(data.pagination || { page: 1, totalPages: 1 });
        setListError('');
      } catch (error) {
        if (!silent) setListError(error.message || 'Failed to load messages');
      } finally {
        if (!silent) setListLoading(false);
      }
    },
    [authFetch]
  );

  useEffect(() => {
    setSelectedUid(null);
    setSelectedMessage(null);
    setReplyOpen(false);
    if (selectedFolder) fetchMessages(selectedFolder, 1);
  }, [selectedFolder, fetchMessages]);

  // Quiet refresh of the folder counts and the current page: on new mail
  // (live event), every REFRESH_MS, and on returning to the tab. Never more
  // often than MIN_GAP_MS, and not while the tab is hidden.
  const refresh = useCallback(() => {
    if (document.visibilityState !== 'visible') return;
    if (Date.now() - lastRefreshRef.current < MIN_GAP_MS) return;
    lastRefreshRef.current = Date.now();
    loadFolders();
    const { folder, page } = viewRef.current;
    if (folder) fetchMessages(folder, page, { silent: true });
  }, [loadFolders, fetchMessages]);

  useEffect(() => {
    lastRefreshRef.current = Date.now();
    const unsubscribe = subscribeRefresh((event) => {
      if (isRefreshFor(event, 'email') && (!event?.type || event.type === 'message_created')) refresh();
    });
    const interval = setInterval(refresh, REFRESH_MS);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      unsubscribe();
      clearInterval(interval);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [subscribeRefresh, refresh]);

  const currentFolderObj = folders.find((folder) => folder.path === selectedFolder);
  const isTrashFolder = normalizeSpecialUse(currentFolderObj?.specialUse) === 'trash';
  const isSentFolder = normalizeSpecialUse(currentFolderObj?.specialUse) === 'sent';
  const trashFolder = folders.find((folder) => normalizeSpecialUse(folder.specialUse) === 'trash');

  const setFlags = async (message, body) => {
    const response = await authFetch(
      `/api/email/mailbox/folders/${encodeURIComponent(selectedFolder)}/messages/${message.uid}/flags`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
    );
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Could not update the message');
    }
  };

  const openMessage = async (message) => {
    setSelectedUid(message.uid);
    setSelectedMessage(null);
    setReplyOpen(false);
    setReplyNotice('');
    setMessageLoading(true);
    setMessageError('');
    setActionError('');
    try {
      const response = await authFetch(`/api/email/mailbox/folders/${encodeURIComponent(selectedFolder)}/messages/${message.uid}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load message');
      setSelectedMessage(data);
      if (message.unseen) {
        setMessages((previous) => previous.map((m) => (m.uid === message.uid ? { ...m, unseen: false } : m)));
        setFlags(message, { addFlags: ['\\Seen'] }).catch(() => {
          // Only the read mark failed; the message is open either way.
        });
      }
    } catch (error) {
      setMessageError(error.message || 'Failed to load message');
    } finally {
      setMessageLoading(false);
    }
  };

  const toggleStar = async (message, event) => {
    event.stopPropagation();
    const starred = isStarred(message.flags);
    const nextFlags = starred
      ? (message.flags || []).filter((flag) => !/flagged/i.test(flag))
      : [...(message.flags || []), '\\Flagged'];
    const apply = (flags) => {
      setMessages((previous) => previous.map((m) => (m.uid === message.uid ? { ...m, flags } : m)));
      setSelectedMessage((previous) => (previous && previous.uid === message.uid ? { ...previous, flags } : previous));
    };
    apply(nextFlags);
    try {
      await setFlags(message, starred ? { removeFlags: ['\\Flagged'] } : { addFlags: ['\\Flagged'] });
    } catch (error) {
      apply(message.flags || []);
      setActionError(error.message);
    }
  };

  const markUnread = async (message) => {
    try {
      await setFlags(message, { removeFlags: ['\\Seen'] });
      setMessages((previous) => previous.map((m) => (m.uid === message.uid ? { ...m, unseen: true } : m)));
      setSelectedUid(null);
      setSelectedMessage(null);
      loadFolders();
    } catch (error) {
      setActionError(error.message);
    }
  };

  // Deleting from a normal folder moves the message to Trash; deleting from
  // Trash itself is permanent, so that asks first.
  const handleDelete = async (message) => {
    setActionError('');
    try {
      let response;
      if (isTrashFolder) {
        if (!window.confirm('Permanently delete this message? This cannot be undone.')) return;
        response = await authFetch(`/api/email/mailbox/folders/${encodeURIComponent(selectedFolder)}/messages/${message.uid}`, {
          method: 'DELETE',
        });
      } else if (trashFolder) {
        response = await authFetch(`/api/email/mailbox/folders/${encodeURIComponent(selectedFolder)}/messages/${message.uid}/move`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ targetFolder: trashFolder.path }),
        });
      } else {
        throw new Error('This mailbox has no Trash folder');
      }
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Could not delete the message');
      }
      setMessages((previous) => previous.filter((m) => m.uid !== message.uid));
      if (selectedUid === message.uid) {
        setSelectedUid(null);
        setSelectedMessage(null);
      }
      loadFolders();
    } catch (error) {
      setActionError(error.message);
    }
  };

  // Attachment links carry a ticket that only opens this message's files
  // (server/email/tickets.js), never the login token.
  const attachmentUrl = (attachment, { download = false } = {}) =>
    `/api/email/mailbox/folders/${encodeURIComponent(selectedFolder)}/messages/${selectedUid}/attachments/${encodeURIComponent(
      attachment.id
    )}?ticket=${encodeURIComponent(selectedMessage?.attachmentTicket || '')}${
      download ? `&name=${encodeURIComponent(attachment.filename || 'attachment')}` : ''
    }`;

  const replyTo = selectedMessage?.from?.address;

  // Hostinger not configured or not reachable: say so plainly, with a retry,
  // instead of three empty columns.
  if (foldersError && folders.length === 0) {
    return (
      <div className="email-mailbox">
        <div className="ew-placeholder">
          <InboxIcon width={40} height={40} />
          <p>Couldn't open the mailbox.</p>
          <p className="ew-muted">{foldersError}</p>
          <button type="button" className="btn btn-secondary btn-small" onClick={() => loadFolders({ selectInbox: true })}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="email-mailbox">
      <nav className="email-mailbox-folders" aria-label="Folders">
        {folders.map((folder) => {
          const meta = folderMeta(folder);
          const Icon = meta.icon;
          return (
            <button
              key={folder.path}
              type="button"
              className={`email-mailbox-folder-btn ${folder.path === selectedFolder ? 'email-mailbox-folder-btn-active' : ''}`}
              onClick={() => setSelectedFolder(folder.path)}
              aria-current={folder.path === selectedFolder ? 'page' : undefined}
            >
              <Icon width={16} height={16} />
              <span>{meta.label}</span>
              {folder.unreadCount > 0 && <span className="ew-unread-badge">{folder.unreadCount}</span>}
            </button>
          );
        })}
      </nav>

      <div className="email-mailbox-list">
        <div className="email-mailbox-list-header">
          <h3>{folderMeta(currentFolderObj).label}</h3>
          <div className="email-mailbox-pagination">
            <button
              type="button"
              className="ew-icon-btn"
              disabled={pagination.page <= 1}
              onClick={() => fetchMessages(selectedFolder, pagination.page - 1)}
              aria-label="Previous page"
            >
              <ChevronLeftIcon width={14} height={14} />
            </button>
            <span>
              {pagination.page} / {pagination.totalPages || 1}
            </span>
            <button
              type="button"
              className="ew-icon-btn"
              disabled={pagination.page >= (pagination.totalPages || 1)}
              onClick={() => fetchMessages(selectedFolder, pagination.page + 1)}
              aria-label="Next page"
            >
              <ChevronRightIcon width={14} height={14} />
            </button>
          </div>
        </div>
        {listLoading ? (
          <ListSkeleton />
        ) : listError ? (
          <div className="ew-banner ew-banner-error">
            {listError}
            <button type="button" className="link-btn" onClick={() => fetchMessages(selectedFolder, pagination.page)}>
              Retry
            </button>
          </div>
        ) : messages.length === 0 ? (
          <p className="ew-empty">No messages in this folder.</p>
        ) : (
          <div className="email-mailbox-items" role="listbox" aria-label="Messages">
            {messages.map((message) => {
              const who = isSentFolder ? (message.to || []).map(addressText).join(', ') : addressText(message.from);
              const starred = isStarred(message.flags);
              return (
                <div
                  key={message.uid}
                  className={`email-mailbox-item ${message.uid === selectedUid ? 'email-mailbox-item-active' : ''} ${
                    message.unseen ? 'email-mailbox-item-unread' : ''
                  }`}
                >
                  <button type="button" role="option" aria-selected={message.uid === selectedUid} className="email-mailbox-item-main" onClick={() => openMessage(message)}>
                    <span className="ew-avatar ew-avatar-sm" style={{ backgroundColor: avatarColor(who) }} aria-hidden="true">
                      {avatarInitials(null, who)}
                    </span>
                    <span className="email-mailbox-item-text">
                      <span className="email-mailbox-item-top">
                        <span className="email-mailbox-item-who">{who || '(unknown)'}</span>
                        <span className="email-mailbox-item-time">{listTimestamp(message.date)}</span>
                      </span>
                      <span className="email-mailbox-item-subject">{message.subject || '(no subject)'}</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`ew-icon-btn ew-icon-btn-xs email-mailbox-star ${starred ? 'email-mailbox-star-active' : ''}`}
                    onClick={(event) => toggleStar(message, event)}
                    aria-label={starred ? 'Unstar' : 'Star'}
                    aria-pressed={starred}
                  >
                    <StarIcon width={14} height={14} filled={starred} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="email-mailbox-reading">
        {foldersError && <div className="ew-banner ew-banner-error">Couldn't refresh the folders: {foldersError}</div>}
        {actionError && (
          <div className="ew-banner ew-banner-error">
            {actionError}
            <button type="button" className="link-btn" onClick={() => setActionError('')}>
              Dismiss
            </button>
          </div>
        )}
        {!selectedUid ? (
          <div className="ew-placeholder">
            <InboxIcon width={40} height={40} />
            <p>Select a message to read it.</p>
          </div>
        ) : messageLoading ? (
          <ListSkeleton rows={4} />
        ) : messageError ? (
          <div className="ew-banner ew-banner-error">{messageError}</div>
        ) : selectedMessage ? (
          <>
            <div className="email-mailbox-reading-header">
              <h3>{selectedMessage.subject || '(no subject)'}</h3>
              <div className="email-mailbox-reading-meta">
                <span>
                  From: {addressText(selectedMessage.from)}{' '}
                  {selectedMessage.from?.address ? `<${selectedMessage.from.address}>` : ''}
                </span>
                <span>To: {(selectedMessage.to || []).map(addressText).join(', ') || '—'}</span>
                <span>{fullTimestamp(selectedMessage.date)}</span>
              </div>
              <div className="email-mailbox-reading-actions">
                {replyTo && (
                  <button type="button" className="btn btn-primary btn-small" onClick={() => setReplyOpen(true)}>
                    <ReplyIcon width={14} height={14} /> Reply
                  </button>
                )}
                <button type="button" className="btn btn-secondary btn-small" onClick={() => markUnread(selectedMessage)}>
                  <MailIcon width={14} height={14} /> Mark unread
                </button>
                <button
                  type="button"
                  className="ew-icon-btn"
                  onClick={() => handleDelete(selectedMessage)}
                  aria-label={isTrashFolder ? 'Delete permanently' : 'Move to Trash'}
                  title={isTrashFolder ? 'Delete permanently' : 'Move to Trash'}
                >
                  <TrashIcon width={15} height={15} />
                </button>
              </div>
            </div>
            {replyNotice && <div className="ew-banner">{replyNotice}</div>}
            <div className="email-mailbox-reading-body">
              {selectedMessage.html ? (
                <iframe
                  title="Email content"
                  sandbox="allow-popups allow-popups-to-escape-sandbox"
                  srcDoc={`<base target="_blank">${withInlineImages(selectedMessage.html, selectedMessage.attachments, (attachment) =>
                    attachmentUrl(attachment)
                  )}`}
                  className="email-mailbox-html-frame"
                />
              ) : (
                <div className="ew-text-body">{selectedMessage.text || <em>(no body)</em>}</div>
              )}
            </div>
            {Array.isArray(selectedMessage.attachments) && selectedMessage.attachments.length > 0 && (
              <div className="ew-msg-files">
                {selectedMessage.attachments.map((attachment) => (
                  <a
                    key={attachment.id}
                    className="ew-file-chip ew-file-chip-link"
                    href={attachmentUrl(attachment, { download: true })}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FileIcon width={13} height={13} />
                    <span className="ew-file-name">{attachment.filename || 'Attachment'}</span>
                    {attachment.sizeBytes != null && <span className="ew-file-meta">{formatFileSize(attachment.sizeBytes)}</span>}
                  </a>
                ))}
              </div>
            )}
            {replyOpen && replyTo && (
              <div className="email-mailbox-reply">
                <EmailComposer
                  key={selectedUid}
                  mode="new"
                  initialTo={replyTo}
                  toLocked
                  initialSubject={replySubject(selectedMessage.subject)}
                  inReplyTo={selectedMessage.messageId || null}
                  inReplyToRef={selectedMessage.messageId ? { folder: selectedFolder, uid: selectedUid } : null}
                  signature={signature}
                  onCancel={() => setReplyOpen(false)}
                  onSent={() => {
                    setReplyOpen(false);
                    setReplyNotice(`Reply sent to ${replyTo}. It's also in Conversations.`);
                  }}
                />
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}

export default EmailMailbox;
